import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Box from '@clasohlson/co-ui-components/Box';
import Column from '@clasohlson/co-ui-components/Column';
import Grid from '@clasohlson/co-ui-components/Grid';
import Typography from '@clasohlson/co-ui-components/Typography';
// import NumberInput from '@clasohlson/co-ui-components/NumberInput';
import Stack from '@clasohlson/co-ui-components/Stack';
import Divider from '@clasohlson/co-ui-components/Divider';
import Textarea from '@clasohlson/co-ui-components/Textarea';
import CheckIcon from '@clasohlson/co-ui-components/Icons/CheckIcon';
import { LineItem } from '../../orderModel';
import { formatSkuNumber, formatPrice, getCountryCodeFromUrl, useTranslation } from '../../utils';
// import { useTranslation } from 'react-i18next';
import { BoxHeader } from '../layout/BoxHeader';
import { CustomRow } from '../layout/CustomRow';
import { CustomColumn } from '../layout/CustomColumn';
import { CustomCheckbox } from '../layout/CustomCheckbox';
import { CustomRadioButton } from '../layout/CustomRadiobutton';
import { CustomTrashIcon } from '../icons/CustomTrashIcon';
import LazyImage from '@clasohlson/co-ui-components/LazyImage';
// import { useGlobalStateValue } from '../../store/globalState';

const ReturnBox = styled(Box)`
  border-width: 1px;
  border-style: solid;
  border-color: #d9d5d5;
  /* background: ${({ theme }) => theme.colors.lightGray}; */
  img {
    height: auto;
    max-width: 90px;
    width: 100%;
  }
  label {
    font-weight: normal;
    font-size: 0.75rem;
    text-transform: none;
    letter-spacing: 0;
  }
  textarea {
    background: #fafafa;
    font-family: 'Clas Ohlson Sans Web', 'Open Sans', 'Arial' !important;
    border-color: #e2e2e2;
    height: 125px;
    font-size: 12px;
    &:focus,
    &:hover {
      background: #f7f7f7;
    }
  }
`;

const RoundIcon = styled.div`
  display: inline-flex;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #fff;
  align-items: center;
  justify-content: center;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

// const CustomNumberInput = styled.div`
//   div {
//     height: 2rem;
//     min-width: 5rem;
//     background: #f7f7f7;
//   }
//   input {
//     font-size: 14px;
//   }
//   button {
//     background: #f7f7f7;
//     color: #000;
//     line-height: 100%;
//     height: auto;
//     &:first-of-type,
//     &:last-of-type {
//       border: 0;
//     }
//   }
//   @media screen and (max-width: 600px) {
//     div {
//       height: 1.75rem;
//     }
//   }
// `;

const MobileFlexReverse = styled.div`
  display: flex;
  justify-content: flex-end;
  @media screen and (max-width: 600px) {
    flex-direction: column-reverse;
    justify-content: flex-end;
    align-items: flex-end;
    > button {
      margin-bottom: 0.25rem;
    }
  }
`;

const ProductTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: bold;
  line-height: 1.23;
`;

const Price = styled.span`
  font-size: 1.125rem;
  font-weight: bold;
  line-height: 1.28;
`;

const StyledColumn = styled(CustomColumn)`
  @media screen and (max-width: 600px) {
    padding: 1rem 0.5rem;
    flex: auto;
    &.image-col {
      width: 25%;
    }
    &.content-col {
      width: 50%;
    }
    &.price-col {
      width: 25%;
    }
  }
`;

const NoFlexColumnRow = styled(Grid)`
  @media screen and (max-width: 600px) {
    > div {
      flex: auto !important;
    }
  }
`;

const StepTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.42;
  letter-spacing: normal;
`;

const StepDescription = styled.span`
  font-size: 10px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.4;
  color: #696969;
`;

interface Props {
  product: LineItem;
  max?: number;
  index: number;
  isValid?: boolean;
  deleteArticle?: (rowNumber: number) => void;
  handleReturnChange: (
    // row: number,
    // qty: number,
    action: string,
    reason: string,
    comment: string | undefined,
  ) => void;
}

export const ReturnProduct: React.FC<Props> = ({
  product,
  max = 1,
  deleteArticle = (ret) => null,
  handleReturnChange,
  index,
  isValid,
}) => {
  const { t } = useTranslation();
  // console.log(product);
  const returnAction = {
    complaint: t('Reklamation'),
    openpurchase: t('Öppet köp'),
  };
  const complaintReasons = [
    { id: 'doa', value: t('Trasig vara') },
    { id: 'transport', value: t('Transportvara') },
    { id: 'delivered', value: t('Fellevererad vara') },
    { id: 'reason-other', value: t('Annat') },
  ];
  const openpurchaseReasons = [
    { id: 'wrong', value: t('Fel produkt') },
    { id: 'wrongsize', value: t('Fel storlek') },
    { id: 'wrongcolor', value: t('Fel färg') },
    { id: 'other', value: t('Annat') },
  ];

  // const {
  //   state: { returns },
  // } = useGlobalStateValue();

  const [reasons, setReasons] = useState<string[]>([]);
  const [action, setAction] = useState<string>(returnAction['complaint']);
  const [notes, setNotes] = useState<string>('');
  // const [qtyToReturn, setQtyToReturn] = useState<number>(0);

  const image = product?.mainImage || `${formatSkuNumber(product.sku)}.jpg`;

  const handleCheckChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      setReasons([...reasons, e.currentTarget.value]);
    } else {
      setReasons([...reasons.filter((v) => v !== e.currentTarget.value)]);
    }
  };

  const handleActionChange = (val: string) => setAction(val);

  useEffect(() => {
    setReasons([]);
  }, [action]);

  useEffect(() => {
    const reasonsStr = reasons.join(', ');
    // const { rowNumber } = product;
    handleReturnChange(action, reasonsStr, notes);
  }, [reasons, action, notes]);
  return (
    <React.Fragment>
      <BoxHeader isValid={isValid}>
        <Grid spacing="medium" alignItems="center">
          <Column lg="6" md="6" sm="6" xs="9">
            <Typography variant="h5" tag="span" weight="heavy" color="#000">
              {t('product')} {index + 1}
            </Typography>
          </Column>
          <Column lg="6" md="6" sm="6" xs="3" alignItems="flex-end" justifyContent="flex-end">
            {isValid && (
              <RoundIcon>
                <CheckIcon color="#80e0a6" fontSize="16" />
              </RoundIcon>
            )}
          </Column>
        </Grid>
      </BoxHeader>
      <ReturnBox>
        <Box topSpacing="medium" leftSpacing="medium" rightSpacing="medium">
          <CustomRow>
            <StyledColumn width={15} className="image-col">
              <LazyImage
                src={`http://cdn.clasohlson.com/productimages/303x303/${image}`}
                alt={product.sku}
              />
              {/* <img src={`http://cdn.clasohlson.com/productimages/303x303/${image}`} /> */}
            </StyledColumn>
            <StyledColumn width={60} className="content-col">
              <ProductTitle>
                <a href={`https://www.clasohlson.com/${product.slug}`}>{product.title}</a>
              </ProductTitle>
              <Typography variant="caption" tag="div">
                {t('artNr')} {product.sku}
              </Typography>
            </StyledColumn>
            <StyledColumn width={25} className="align-right price-col">
              <Stack spacing="medium">
                <MobileFlexReverse>
                  {/* <CustomNumberInput>
                    <NumberInput
                      min={0}
                      max={max}
                      value={qtyToReturn}
                      label={t('numberOfItems')}
                      handleChange={(num) => setQtyToReturn(num)}
                      hideLabel
                      data-testid={`${product.sku}-returnQty`}
                    />
                  </CustomNumberInput> */}
                  <DeleteButton onClick={() => deleteArticle(product.rowNumber)}>
                    <CustomTrashIcon color="#cc2262" />
                  </DeleteButton>
                </MobileFlexReverse>
                <Box justifyContent="flex-end" alignItems="flex-end" className="price">
                  <Price>
                    {formatPrice(
                      product.price.sumPriceIncludingDiscounts.priceIncludingVat,
                      getCountryCodeFromUrl(),
                    )}
                  </Price>
                  <Typography variant="caption" tag="span" align="right" color="#666666">
                    {t('incVat')}
                  </Typography>
                </Box>
              </Stack>
            </StyledColumn>
          </CustomRow>
        </Box>
        <Divider spacing="none" color="#d9d5d5" />
        <Box topSpacing="small" bottomSpacing="small" leftSpacing="small" rightSpacing="small">
          <StepTitle>{t('reasonForReturn')}</StepTitle>
          <StepDescription>{t('selectReason')}</StepDescription>
          <div style={{ marginTop: '1rem' }}>
            <Grid spacing="large" justifyContent="flex-start" alignItems="flex-start">
              {Object.keys(returnAction).map((key, i) => (
                <Column key={key} lg="3" md="3" sm="3" xs="6">
                  <CustomRadioButton
                    key={`${product.rowNumber}-${key}`}
                    id={`${product.rowNumber}-${key}`}
                    label={returnAction[key]}
                    name={`${product.rowNumber}-reason`}
                    value={returnAction[key]}
                    onChange={(e) => handleActionChange(e.currentTarget.value)}
                    checked={action === returnAction[key]}
                    testId={`${product.sku}-${key}`}
                  />
                </Column>
              ))}
            </Grid>
          </div>
        </Box>
        <Divider spacing="none" color="#d9d5d5" />
        <Box topSpacing="small" bottomSpacing="small" leftSpacing="small" rightSpacing="small">
          <StepTitle>
            {action === t(returnAction.complaint) && t('selectAction')}
            {action === t(returnAction.openpurchase) && t('selectActionOpenPurchase')}
            {/* {t('selectAction')}{' '} */}
            <Typography variant="caption" tag="span" display="inline" color="#c5014a">
              *
            </Typography>
          </StepTitle>
          <StepDescription>{t('mustSelectOneReason')}</StepDescription>
          <div style={{ marginTop: '1rem' }}>
            {action === t(returnAction.complaint) && (
              <NoFlexColumnRow spacing="large" alignItems="flex-start" justifyContent="flex-start">
                {complaintReasons.map((reason) => (
                  <Column key={reason.id} lg="3" md="3" sm="3" xs="6">
                    <CustomCheckbox
                      key={`${product.sku}-${reason.id}`}
                      onChange={(e) => handleCheckChange(e)}
                      id={`${product.sku}-${reason.id}`}
                      label={reason.value}
                      value={reason.value}
                      testId={`${product.sku}-${reason.id}`}
                    />
                  </Column>
                ))}
              </NoFlexColumnRow>
            )}
            {action === t(returnAction.openpurchase) && (
              <NoFlexColumnRow spacing="large" alignItems="flex-start" justifyContent="flex-start">
                {openpurchaseReasons.map((reason) => (
                  <Column key={reason.id} lg="3" md="3" sm="3" xs="6">
                    <CustomCheckbox
                      key={`${product.sku}-${reason.id}`}
                      onChange={(e) => handleCheckChange(e)}
                      id={`${product.sku}-${reason.id}`}
                      label={reason.value}
                      value={reason.value}
                      testId={`${product.sku}-${reason.id}`}
                    />
                  </Column>
                ))}
              </NoFlexColumnRow>
            )}
          </div>
          {/* {vals.length === 0 && (
            <Typography variant="p" tag="div" color="#cc2262">
              Du måste välja minst en av åtgärderna ovan
            </Typography>
          )} */}
        </Box>
        <Divider spacing="none" color="#d9d5d5" />
        <Box topSpacing="small" bottomSpacing="small" leftSpacing="small" rightSpacing="small">
          <StepTitle>
            {t('additionalInformation')}{' '}
            <Typography variant="caption" tag="span" display="inline" color="#c5014a">
              *
            </Typography>
          </StepTitle>
          <StepDescription>{t('mustEnterNotes')}</StepDescription>
          <Divider color="#FFFFFF" spacing="xxsmall" />
          <Textarea
            label={t('additionalInformation')}
            hideLabel
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            data-testid={`${product.sku}-comment`}
          />
        </Box>
      </ReturnBox>
    </React.Fragment>
  );
};
